import { useMutation, UseMutationResult, useQueryClient } from 'react-query';

import { useAuth } from 'src/hooks';
import { useProgressStore } from 'src/hooks/useProgressStore';

import { changeUserEmail } from 'src/utils/urls';

type ApiErrorItem = {
  Code: string;
  FieldName: string;
  Message: string;
};

type TResult<TData = unknown> = {
  Errors?: ApiErrorItem[];
  ErrorId?: string;
  IsSuccess: boolean;
  StatusCode: number;
  Data?: TData;
};

type TVariables = {
  UserId: string;
  NewEmailId: string;
  fromSettings?: boolean;
};

type TError = Error;
type TSnapshot = unknown;

async function changeEmail({
  requestData,
  token,
  tokenType,
}: {
  requestData: TVariables;
  token: string;
  tokenType: string;
}): Promise<TResult> {
  const response = await fetch(changeUserEmail, {
    method: 'POST',
    headers: {
      Authorization: `${tokenType} ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });

  if (response.status === 200 && response.redirected) {
    throw new Error('Session expired');
  }

  let data: TResult | null = null;

  try {
    const text = await response.text();
    data = text ? JSON.parse(text) : null;
  } catch (e) {
    console.error('JSON parse failed:', e);
  }

  if (!data && response.ok) {
    return {
      IsSuccess: true,
      StatusCode: response.status,
    };
  }

  if (!data) {
    throw new Error('Invalid server response');
  }

  return data;
}

function useChangeEmail(): UseMutationResult<
  TResult,
  TError,
  TVariables,
  TSnapshot
> {
  const { token, tokenType } = useAuth();
  const queryClient = useQueryClient();
  const { setLoading } = useProgressStore();

  return useMutation(
    async (params: TVariables) => {
      setLoading('isChangeEmailLoading', true);

      const result = await changeEmail({
        requestData: params,
        token,
        tokenType,
      });

      if (!result.IsSuccess) {
        const errorMessage =
          result.Errors?.map(err => err.Message).join(', ') ||
          'Failed to change email';

        throw new Error(errorMessage);
      }

      if (!params.fromSettings) {
        queryClient.invalidateQueries('get_users_list');
      }

      return result;
    },
    {
      onSettled: () => {
        setLoading('isChangeEmailLoading', false);
      },
    },
  );
}

export { useChangeEmail };
